/**
 * Admin user invite / edit form.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MdCheck, MdExpandMore, MdEmail, MdSave } from 'react-icons/md';
import { useToast } from '../../components/shared/Toast';
import { cn } from '../../lib/utils';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface User {
  _id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phone?: string;
  employeeId?: string;
  department?: string;
  designation?: string;
  reportingManager?: string;
  location?: string;
  portalType: string;
  role?: string;
  roleId?: string | { _id: string; name: string; key: string; permissions?: string[] };
  isActive?: boolean;
}

interface UserFormProps {
  user?: User | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface RoleDto {
  _id?: string;
  name: string;
  key: string;
  portalType: string;
  permissions: string[];
  description?: string;
}

interface RoleOption {
  id: string;
  mongoId?: string;
  key: string;
  name: string;
  permissions: string[];
  description?: string;
}

const DEFAULT_ADMIN_ROLES: RoleOption[] = [
  { id: 'admin_superuser', key: 'admin_superuser', name: 'Super Admin', permissions: ['*'], description: 'Full platform access across every admin module' },
  { id: 'admin_system_admin', key: 'admin_system_admin', name: 'System Admin', permissions: ['users:*', 'roles:*', 'settings:update', 'logs:view'], description: 'Manage users, roles, settings, and activity logs' },
  { id: 'admin_finance_admin', key: 'admin_finance_admin', name: 'Finance Admin', permissions: ['invoices:*', 'transactions:read', 'reports:generate'], description: 'Handle billing, transactions, and financial reporting' },
  { id: 'admin_hr_admin', key: 'admin_hr_admin', name: 'HR Admin', permissions: ['employees:*', 'attendance:read', 'leaves:approve'], description: 'Maintain employee records, attendance, and leave approvals' },
  { id: 'admin_auditor', key: 'admin_auditor', name: 'Auditor', permissions: ['logs:read', 'invoices:read', 'users:read'], description: 'Read-only access for compliance and audits' },
  { id: 'admin_support_agent', key: 'admin_support_agent', name: 'Support Agent', permissions: ['tickets:*', 'customers:read'], description: 'Respond to support tickets and review customer info' },
];

const formatRoleName = (roleKey: string) =>
  roleKey
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const fieldClass =
  'w-full h-10 px-3 text-sm rounded-md border bg-[hsl(var(--background))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/20 focus:border-[hsl(var(--primary))] transition-colors';

const labelClass = 'mb-1.5 block text-xs font-medium text-[hsl(var(--foreground))]';

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-md border border-[hsl(var(--border))]">
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-3 py-2">
        <h3 className="text-xs font-semibold tracking-wide text-[hsl(var(--foreground))]">{title}</h3>
      </div>
      <div className="p-3">{children}</div>
    </section>
  );
}

function RoleSelect({
  options,
  value,
  onChange,
  disabled,
  error,
  loading,
}: {
  options: RoleOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  const selected = options.find((r) => (r.mongoId || r.key) === value || r.id === value);

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const gap = 4;
    const width = Math.min(Math.max(rect.width, 320), viewportW - 16);
    let left = rect.left;
    if (left + width > viewportW - 8) left = Math.max(8, viewportW - width - 8);
    const spaceBelow = Math.max(128, viewportH - rect.bottom - 12);

    setMenuStyle({
      position: 'fixed',
      zIndex: 2300,
      top: rect.bottom + gap,
      left,
      width,
      maxHeight: Math.min(280, spaceBelow),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onMove = () => updateMenuPosition();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          fieldClass,
          'flex items-center justify-between gap-2 text-left',
          error ? 'border-red-400' : 'border-[hsl(var(--border))]',
          (disabled || loading) && 'cursor-not-allowed opacity-60'
        )}
      >
        <span className={cn('truncate', !selected && 'text-[hsl(var(--muted-foreground))]')}>
          {loading ? 'Loading roles…' : selected?.name || 'Select a role'}
        </span>
        <MdExpandMore className="h-4 w-4 shrink-0 text-[hsl(var(--muted-foreground))]" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              style={menuStyle}
              className="overflow-y-auto rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-1 shadow-xl"
            >
              {options.map((role) => {
                const optionValue = role.mongoId || role.key;
                const isSelected = optionValue === value || role.id === value;
                return (
                  <button
                    key={role.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onChange(optionValue);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-2 text-left transition-colors',
                      isSelected
                        ? 'bg-[hsl(var(--primary))]/10'
                        : 'hover:bg-[hsl(var(--muted))]'
                    )}
                  >
                    <MdCheck
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0',
                        isSelected ? 'text-[hsl(var(--primary))]' : 'text-transparent'
                      )}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-[hsl(var(--foreground))]">
                        {role.name}
                      </span>
                      {role.description ? (
                        <span className="mt-0.5 block text-xs leading-snug text-[hsl(var(--muted-foreground))]">
                          {role.description}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export function UserForm({ user, onSuccess, onCancel }: UserFormProps) {
  const toast = useToast();

  const [formData, setFormData] = useState({
    email: user?.email || '',
    fullName: user?.fullName || (user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : ''),
    phone: user?.phone || '',
    employeeId: user?.employeeId || '',
    department: user?.department || '',
    designation: user?.designation || '',
    reportingManager: user?.reportingManager || '',
    location: user?.location || '',
    portalType: 'admin', // Fixed - only admin
    roleId: typeof user?.roleId === 'string' ? user.roleId : user?.roleId?._id || '',
    roleKey: (typeof user?.roleId === 'object' ? user.roleId?.key : user?.role) || '',
    isActive: user?.isActive ?? true,
  });
  const [baseline, setBaseline] = useState(formData);

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/admin/users/next-employee-id`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
        if (!response.ok) return;
        const json = await response.json();
        const nextId = json.data?.employeeId;
        if (!cancelled && nextId) {
          setFormData((p) => ({ ...p, employeeId: nextId }));
        }
      } catch {
        // Backend still allocates on submit.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (user) {
      const next = {
        email: user.email || '',
        fullName: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        phone: user.phone || '',
        employeeId: user.employeeId || '',
        department: user.department || '',
        designation: user.designation || '',
        reportingManager: user.reportingManager || '',
        location: user.location || '',
        portalType: 'admin',
        roleId: typeof user.roleId === 'string' ? user.roleId : user.roleId?._id || '',
        roleKey: (typeof user.roleId === 'object' ? user.roleId?.key : user?.role) || '',
        isActive: user.isActive ?? true,
      };
      setFormData(next);
      setBaseline(next);
    }
  }, [user]);

  const {
    data: rolesResponse,
    isLoading: isRolesLoading,
  } = useQuery({
    queryKey: ['roles', 'admin'],
    queryFn: async (): Promise<RoleDto[]> => {
      try {
        const response = await fetch(`${API_URL}/api/v1/roles?portalType=admin`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
        if (!response.ok) throw new Error();
        const data = await response.json();
        return data.data || [];
      } catch {
        return DEFAULT_ADMIN_ROLES.map((r) => ({
          name: r.name,
          key: r.key,
          permissions: r.permissions,
          portalType: 'admin',
        }));
      }
    },
  });

  const roleOptions: RoleOption[] = useMemo(() => {
    let options = rolesResponse?.length
      ? rolesResponse.map((r) => ({
          id: r._id || r.key,
          mongoId: r._id,
          key: r.key,
          name: r.name,
          permissions: r.permissions || [],
          description: r.description,
        }))
      : DEFAULT_ADMIN_ROLES;

    const currentRoleKey = user?.role;
    if (currentRoleKey && !options.some((o) => o.key === currentRoleKey)) {
      options.push({
        id: currentRoleKey,
        key: currentRoleKey,
        name: formatRoleName(currentRoleKey),
        permissions: [],
      });
    }

    return options;
  }, [rolesResponse, user]);

  useEffect(() => {
    if (user || roleOptions.length === 0) return;
    if (!formData.roleId && !formData.roleKey) {
      const first = roleOptions[0];
      setFormData((p) => ({
        ...p,
        roleId: first.mongoId || '',
        roleKey: first.key,
      }));
    }
  }, [roleOptions, user, formData.roleId, formData.roleKey]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch(`${API_URL}/api/v1/admin/users/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(data),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || json.message || 'Failed to invite user');
      return json;
    },
    onSuccess: (data) => {
      toast.success(data.data?.temporaryPassword ? `Invitation sent. Temp password: ${data.data.temporaryPassword}` : 'Invitation sent!');
      onSuccess();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch(`${API_URL}/api/v1/admin/users/${user!._id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ _id: user!._id, ...data }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || json.message || 'Failed to update user');
      return json;
    },
    onSuccess: () => {
      toast.success('User updated successfully!');
      onSuccess();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const selectedRoleValue = formData.roleId || formData.roleKey || '';
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isDirty = JSON.stringify(formData) !== JSON.stringify(baseline);
  const saveDisabled = isSaving || (!!user && !isDirty);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!formData.email.includes('@')) return setErrors((p) => ({ ...p, email: 'Valid email required' }));
    if (!formData.fullName.trim()) return setErrors((p) => ({ ...p, fullName: 'Full name required' }));
    if (!formData.roleId && !formData.roleKey) return setErrors((p) => ({ ...p, role: 'Role required' }));
    if (user && !isDirty) return;

    const parts = formData.fullName.trim().split(/\s+/);
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ') || parts[0];

    const payload: any = {
      email: formData.email.trim().toLowerCase(),
      firstName,
      lastName,
      phone: formData.phone.trim() || undefined,
      department: formData.department.trim() || undefined,
      designation: formData.designation.trim() || undefined,
      reportingTo: formData.reportingManager.trim() || undefined,
      location: formData.location.trim() || undefined,
      portalType: 'admin',
      roleId: formData.roleId || undefined,
      role: formData.roleKey,
      isActive: formData.isActive,
    };

    user?._id ? updateMutation.mutate(payload) : createMutation.mutate(payload);
  };

  const handleRoleSelect = (value: string) => {
    const selected = roleOptions.find((r) => r.mongoId === value || r.id === value || r.key === value);
    if (selected) {
      setFormData((p) => ({ ...p, roleId: selected.mongoId || '', roleKey: selected.key }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-3">
      <FormSection title="Account details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>
              Work email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
              disabled={!!user}
              className={cn(
                fieldClass,
                errors.email ? 'border-red-400' : 'border-[hsl(var(--border))]',
                user && 'bg-[hsl(var(--muted))] cursor-not-allowed'
              )}
              placeholder="name@euroasiann.com"
              autoComplete="email"
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>

          <div>
            <label className={labelClass}>
              Full name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData((p) => ({ ...p, fullName: e.target.value }))}
              className={cn(fieldClass, errors.fullName ? 'border-red-400' : 'border-[hsl(var(--border))]')}
              placeholder="First and last name"
              autoComplete="name"
            />
            {errors.fullName && <p className="mt-1 text-xs text-red-600">{errors.fullName}</p>}
          </div>

          <div>
            <label className={labelClass}>Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
              className={cn(fieldClass, 'border-[hsl(var(--border))]')}
              placeholder="+91 98765 43210"
              autoComplete="tel"
            />
          </div>

          <div>
            <label className={labelClass}>Employee ID</label>
            <input
              type="text"
              value={formData.employeeId}
              readOnly
              className={cn(fieldClass, 'border-[hsl(var(--border))] bg-[hsl(var(--muted))] cursor-not-allowed')}
              placeholder="Assigned on invite"
            />
          </div>
        </div>
      </FormSection>

      <FormSection title="Role">
        <div>
          <label className={labelClass}>
            Role <span className="text-red-500">*</span>
          </label>
          <RoleSelect
            options={roleOptions}
            value={selectedRoleValue}
            onChange={handleRoleSelect}
            disabled={isRolesLoading}
            loading={isRolesLoading}
            error={!!errors.role}
          />
          {errors.role && <p className="mt-1 text-xs text-red-600">{errors.role}</p>}
        </div>
      </FormSection>

      {user ? (
        <FormSection title="Access">
          <label
            htmlFor="isActive"
            className="flex items-center gap-2.5 cursor-pointer"
          >
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
              className="h-4 w-4 rounded border-[hsl(var(--border))] text-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
            />
            <span className="text-sm text-[hsl(var(--foreground))]">Account is active</span>
          </label>
        </FormSection>
      ) : null}

      <FormSection title="Employment">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Department</label>
            <input
              type="text"
              value={formData.department}
              onChange={(e) => setFormData((p) => ({ ...p, department: e.target.value }))}
              className={cn(fieldClass, 'border-[hsl(var(--border))]')}
              placeholder="Administration"
            />
          </div>

          <div>
            <label className={labelClass}>Designation</label>
            <input
              type="text"
              value={formData.designation}
              onChange={(e) => setFormData((p) => ({ ...p, designation: e.target.value }))}
              className={cn(fieldClass, 'border-[hsl(var(--border))]')}
              placeholder="System Administrator"
            />
          </div>

          <div>
            <label className={labelClass}>Reporting manager</label>
            <input
              type="text"
              value={formData.reportingManager}
              onChange={(e) => setFormData((p) => ({ ...p, reportingManager: e.target.value }))}
              className={cn(fieldClass, 'border-[hsl(var(--border))]')}
              placeholder="Manager email or name"
            />
          </div>

          <div>
            <label className={labelClass}>Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData((p) => ({ ...p, location: e.target.value }))}
              className={cn(fieldClass, 'border-[hsl(var(--border))]')}
              placeholder="Head Office"
            />
          </div>
        </div>
      </FormSection>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="h-9 px-4 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saveDisabled}
          title={user && !isDirty ? 'Change a field to enable Save' : undefined}
          className="h-9 px-4 rounded-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {user ? <MdSave className="h-4 w-4" /> : <MdEmail className="h-4 w-4" />}
          {isSaving ? (user ? 'Saving…' : 'Sending…') : user ? 'Save changes' : 'Send invitation'}
        </button>
      </div>
    </form>
  );
}
